"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { trackCTAClick } from "@/app/lib/analytics";
import { PLAY_STORE_URL } from "@/app/lib/constants";

const NAV_LINKS = [
  { href: "/#capitulos", label: "Cómo funciona" },
  { href: "/#que-podes-hacer", label: "Cómo se usa" },
  { href: "/#simulador", label: "Simulador" },
  { href: "/calculadora", label: "Calculadora" },
  { href: "/#lo-que-viene", label: "Inversiones" },
  { href: "/#equipo", label: "Equipo" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled || menuOpen ? "navbar-scrolled" : ""
        }`}
      >
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between">
          <Link
            href="/"
            className="relative z-[70] flex items-center gap-2 group"
          >
            <img
              src="/finbo-wordmark.svg"
              alt="Finbo"
              className="h-5 sm:h-[22px] w-auto"
            />
            <span className="w-1.5 h-1.5 rounded-full bg-primary transition-all group-hover:bg-primary-light" />
          </Link>

          <div className="hidden lg:flex items-center gap-6 lg:gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-[13.5px] text-white/55 hover:text-white transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
            <Link
              href="/blog"
              className="text-[13.5px] text-white/55 hover:text-white transition-colors duration-300"
            >
              Blog
            </Link>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary !py-2 !px-4 !text-[13.5px]"
              onClick={() => trackCTAClick("navbar_download", "navbar")}
            >
              <Download size={14} />
              Descargar
            </a>
          </div>

          <button
            className="relative z-[70] lg:hidden p-2 -mr-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menú"
          >
            {menuOpen ? (
              <X size={24} className="text-white" />
            ) : (
              <Menu size={24} className="text-white" />
            )}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] lg:hidden bg-[#0A0F14]/98 backdrop-blur-3xl overflow-y-auto"
          >
            <div className="min-h-full flex flex-col items-center justify-center gap-6 px-8 py-24">
              {[...NAV_LINKS, { href: "/blog", label: "Blog" }].map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.12 + i * 0.07,
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="text-2xl sm:text-3xl font-medium text-white/90 hover:text-primary transition-colors tracking-tight"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </motion.a>
              ))}

              <motion.a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="btn-primary mt-6 text-lg !py-4 !px-10"
                onClick={() => {
                  trackCTAClick("mobile_menu_download", "mobile_menu");
                  setMenuOpen(false);
                }}
              >
                <Download size={20} />
                Descargar gratis
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
